import { Component, Input } from '@angular/core';
import { InputGeneral } from '../../shared/inputs/input-general/input-general';
import { MatButtonModule } from '@angular/material/button';
import { InputGeneralCalendarComponent } from '../../shared/inputs/input-general-calendar/input-general-calendar.component';
import { TableGeneralSimpleComponent } from '../../shared/components-generals/table-general-simple/table-general-simple.component';
import { FormControl, Validators } from '@angular/forms';
import { GeneralService } from '../../shared/services/general.service';
import { MigrationMaintenanceService } from '../../shared/services/migration-maintenance.service';
import { MatTableDataSource } from '@angular/material/table';
import { QuotationContractDates } from '../../shared/models/maintenance/migration-maintenance.model';
import { LoadGeneralComponent } from '../../shared/components-generals/load-general/load-general.component';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-modify-dates',
  imports: [
    MatButtonModule,
    InputGeneralCalendarComponent,
    TableGeneralSimpleComponent,
    LoadGeneralComponent,
    TranslateModule,
  ],
  templateUrl: './modify-dates.html',
  styleUrl: './modify-dates.css',
})
export class ModifyDates {
  @Input() quotationId: number = 0;
  public loadService: boolean = false;
  public step1: boolean = true;
  public step2: boolean = false;
  public step3: boolean = false;
  public quotationContract: QuotationContractDates | null = null;
  public searchQuotation: FormControl = new FormControl(this.quotationId, [
    Validators.required,
    Validators.min(1),
  ]);
  public newDate: FormControl = new FormControl('', [Validators.required]);
  public newDateEnd: Date | null = null;
  public dataSource = new MatTableDataSource<QuotationContractDates>([]);

  public headers = [
    { value: 'idCotizacion', description: 'Cotización' },
    { value: 'nombreCompleto', description: 'Nombre Completo' },
    { value: 'desPlazo1', description: 'Plazo' },
    { value: 'fechaInicio', description: 'Fecha Inicio' },
    { value: 'fechaTermino', description: 'Fecha Termino' },
    { value: 'status', description: 'Estado' },
    { value: 'corto', description: 'Corto' },
    { value: 'canModify', description: '¿Puede Modificar?' },
  ];

  constructor(
    private generalService: GeneralService,
    private migrationMaintenance: MigrationMaintenanceService,
  ) {
    this.generalService.subscribeFormSimple(this.searchQuotation).subscribe(() => {
      if (this.searchQuotation.valid) {
        this.loadService = true;
        this.migrationMaintenance
          .GetQuotationContractDates(this.searchQuotation.value)
          .subscribe((dates) => {
            this.quotationContract = dates;
            this.loadService = false;
            if (dates) {
              this.newDate.setValue('');
              this.newDateEnd = null;

              this.step1 = false;
              this.step2 = true;
              this.step3 = false;
              this.dataSource.data = [dates];
            }
          });
      }
    });

    this.generalService.subscribeFormSimple(this.newDate).subscribe(() => {
      if (this.newDate.valid) {
        const baseDate = new Date(this.newDate.value);
        const monthsToAdd = this.quotationContract
          ? parseInt(this.quotationContract.desPlazo1 ?? '0', 10)
          : 0;

        if (Number.isNaN(baseDate.getTime())) {
          this.newDateEnd = null;
          return;
        }

        this.newDateEnd = this.addMonths(baseDate, monthsToAdd);
      } else {
        this.newDateEnd = null;
      }
    });
  }

  ngOnInit() {
    if (this.quotationId) {
      this.searchQuotation.setValue(`${this.quotationId}`);
    }
  }

  public selectQuotation(element: any) {
    if (!element.canModify) {
      this.generalService.viewSnacAlert(
        'No es posible modificar las fechas de esta cotización',
        'error',
        'Error',
      );
    } else {
      this.step2 = false;
      this.step3 = true;
    }
  }

  private addMonths(dateInput: string | Date, months: number): Date {
    const date = new Date(dateInput);

    if (isNaN(date.getTime())) {
      throw new Error('Fecha inválida');
    }

    const originalDay = date.getDate();

    // crear nueva fecha segura
    const result = new Date(date);

    // mover al día 1 para evitar saltos raros
    result.setDate(1);

    // sumar meses reales
    result.setMonth(result.getMonth() + months);

    // obtener último día válido del mes destino
    const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();

    // restaurar día original o el máximo posible
    result.setDate(Math.min(originalDay, lastDayOfMonth));

    return result;
  }

  public saveNewDate() {
    this.loadService = true;
    this.migrationMaintenance
      .quotationContractDates({
        fechaInicio: this.newDate.value,
        idCotizacion: this.quotationContract?.idCotizacion ?? 0,
        plazo: parseInt(this.quotationContract?.desPlazo1 ?? '0', 10),
      })
      .pipe(
        finalize(() => {
          this.resetForm();
        }),
      )
      .subscribe((response) => {
        if (response) {
          this.generalService.viewSnacAlert('Fecha guardada correctamente', 'normal', 'Éxito');
        } else {
          this.generalService.viewSnacAlert('Error al guardar la fecha', 'error', 'Error');
        }
      });
  }

  public deleteQuotation(): void {
    this.quotationContract = null;
    this.newDate.setValue('');
    this.searchQuotation.setValue('');
    this.newDateEnd = null;
    this.step1 = true;
    this.step2 = false;
    this.step3 = false;
    this.dataSource.data = [];
  }

  public resetForm(): void {
    if (this.quotationId) {
      this.searchQuotation.setValue(``);
      setTimeout(() => {
        this.searchQuotation.setValue(`${this.quotationId}`);
      }, 700);
    }
  }
}
